import { createRoute, Link, Navigate } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { useAuthState } from '@/lib/auth-state'
import { readLastProjectId, resolveLandingProjectId } from '@/lib/last-project'
import { ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: RootIndex,
})

function RootIndex() {
  const { me, status } = useAuthState()

  if (status === 'loading' || !me) {
    return null
  }

  // Land in the project the user opened last. `me.project` is the server's
  // pick (users.last_project_id, validated); the local hint covers a visit
  // made after `me` was loaded. Both fall back to the first project.
  const projectId = resolveLandingProjectId({
    serverProjectId: me.project?.id,
    memberProjectIds: me.memberships.projects.map((p) => p.projectId),
    storedProjectId: readLastProjectId(me.user.id),
  })

  if (projectId) {
    return <Navigate to="/$projectId/chat" params={{ projectId }} replace />
  }

  return <NoProjectLanding isSuperAdmin={me.isSuperAdmin} />
}

function NoProjectLanding({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="text-center max-w-md space-y-4">
        <h2 className="text-xl font-semibold text-foreground">{t('home.noProjectsTitle')}</h2>
        <p className="text-muted-foreground">{t('home.noProjectsBody')}</p>
        {isSuperAdmin && (
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <ShieldCheck className="h-4 w-4" />
            {t('home.goToAdmin')}
          </Link>
        )}
      </div>
    </div>
  )
}
